/**
 * Honeybadger Error Tracking Configuration
 * @memberof @albert-io/notifier
 */

import React from 'react';
import {getEnvironment} from '@albert-io/environment';
import {Honeybadger, HoneybadgerErrorBoundary} from '@honeybadger-io/react';

import {ERROR_TYPES} from 'client/constants';
import ErrorPage from 'client/static/Error/ErrorPage.react';
import sessionStore from 'client/Session/SessionStore';
import {shouldReportError, isCurrentUrlMatch} from './filters';

// configuration

const CONTENT_FILTERS = [
  'token',
  'email',
  'username',
  'password',
  'verify_old_password',
  'password_reset_link',
  'verify_password_reset_link',
  'stripe_customer_id',
  'first_name',
  'last_name',
  'salutation',
  'consent_email'
];

const getReleaseVersion = () => {
  const path = process.env.STATIC_ASSETS_APP_PATH || '';
  if (!path) return '';

  const segments = path.split('/').filter((segment) => segment.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : '';
};

const RELEASE_VERSION = getReleaseVersion();

// build context for error (environment, session)

/**
 * Build environment tags as an array for Honeybadger
 * Honeybadger now accepts tags as an array instead of comma-separated string
 *
 * @param {string} customTags - Custom tags from notifier.notify call (single or comma-separated)
 * @param {string} userAgent - Browser user agent string
 * @returns {Array<string>} Array of tags with no empty values
 */
const buildEnvironmentTags = (customTags, userAgent) => {
  const isIpad = /iPad/.test(userAgent);
  const isDennis = isCurrentUrlMatch('/dennis');

  const customTagsArray = customTags
    ? customTags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    : [];

  const tags = [
    RELEASE_VERSION,
    sessionStore.hasValidSession() ? sessionStore.getAccountType() : 'unauthenticated',
    ...(isIpad ? ['ipad'] : []),
    ...(isDennis ? ['dennis'] : []),
    ...customTagsArray
  ];

  // Remove duplicates and empty values
  return [...new Set(tags.filter(Boolean))];
};

const buildErrorContext = (existingContext) => {
  const hasTranslate = global.document.getElementsByClassName('translated-ltr').length > 0;

  return {
    ...existingContext,
    user_id: sessionStore.hasValidSession() ? sessionStore.getUserId() : null,
    hasTranslate,
    route: {pathname: window.location.pathname}
  };
};

const initializeHoneybadger = () => {
  Honeybadger.configure({
    apiKey: process.env.HONEYBADGER_API_KEY,
    environment: getEnvironment(),
    filters: CONTENT_FILTERS,
    reportData: true,
    revision: RELEASE_VERSION,
    maxErrors: 20,
    breadcrumbsEnabled: {
      dom: true,
      network: true,
      navigation: true,
      console: false
    },
    maxBreadcrumbs: 50
  });

  Honeybadger.beforeNotify((notice) => {
    const userAgent = navigator.userAgent || '';
    notice.tags = buildEnvironmentTags(notice.tags, userAgent);
    notice.context = buildErrorContext(notice.context);
    return shouldReportError(notice);
  });
};

if (process.env.HONEYBADGER_API_KEY) {
  initializeHoneybadger();
}

/**
 * Report errors to Honeybadger - lightweight wrapper around Honeybadger.notify
 *
 * @example
 * notifier.notify(error, {
 *   message: 'Failed to save',
 *   tags: 'ComponentName',
 *   fingerprint: 'unique-error-id'
 * })
 */
const notifier = {
  notify(error, options) {
    if (!process.env.HONEYBADGER_API_KEY) {
      console.log('Honeybadger API key not configured; skipping error report.');
      return;
    }

    Honeybadger.notify(error, options);
  }
};

export const ErrorBoundary = ({children}) => (
  <HoneybadgerErrorBoundary
    honeybadger={Honeybadger}
    ErrorComponent={() => <ErrorPage errorCode={ERROR_TYPES.GENERIC} />}
  >
    {children}
  </HoneybadgerErrorBoundary>
);

export default notifier;
